import React from 'react';
import { connect } from 'react-redux';
// Store
import { IRootState } from '../../redux';
import { Dispatch } from 'redux';
import { selectMailsAction, setTouchSelectActive } from '../../redux/menubar/actions';
import { setSelectedDocuments } from '../../redux/mailbox/actions';
// Components
import MenuBarButton from './MenuBarButton/MenuBarButton';
// Interfaces
import styles from './menubar.module.scss';
import { ISelectedMessage, IMessage } from '../../interfaces/message.interfaces';
// Helpers
import { isMediaSizeLessThanLG, isMediaSizeLessThanMD } from '../../helpers/layout';
import { isTouchScreen } from '../../helpers/browser';
import { isSentFolder } from '../../helpers/folder';
import { getCurrentLanguage, getSelectedDocuments } from '../../helpers/general';
import { isAllowed } from '../../helpers/access';
import { IsMailDeleteAllowed, IsMailReplyAllowed } from '../../helpers/mailAction';
// Config
import { ConfigContext } from '../../providers';
// Enums
import {
    MenuBarButtonType,
    MoveToFolderItems,
    MoreActionsItems,
    MessageState,
    PermissionType,
    SelectAllItems,
} from '../../enums';

const mobileHeaderHeight = 50;

interface IMenuBarProps {
    className?: string;
    onFolderAction?: (action: MoveToFolderItems, folderId: string) => void;
    onMoreAction?: (action: MoreActionsItems, value: string) => void;
    onMenuBarButtonAction: (action: MenuBarButtonType) => void;
    onSelectMailsAction?: (action: SelectAllItems) => void;
    hideSelectAll?: boolean;
    hideMore?: boolean;
    hideClose?: boolean;
    hidePrint?: boolean;
    hideMoveToFolder?: boolean;
    hideReply?: boolean;
    hideForward?: boolean;
    hideArchive?: boolean;
    hideDelete?: boolean;
    hideSave?: boolean;
    hideSend?: boolean;
    oneDocumentView?: boolean;
}

class MenuBar extends React.Component<ReduxType> {
    componentDidUpdate(prevProps: ReduxType): void {
        if (
            (this.props.isSenderClicked
                ? prevProps.selectSenderFolder.originalRecords.length !==
                  this.props.selectSenderFolder.originalRecords.length
                : prevProps.documents.length !== this.props.documents.length) &&
            this.props.selectMailItemsAction !== undefined &&
            this.props.selectMailItemsAction !== null
        ) {
            this.handleItemSelected(MenuBarButtonType.SELECT_ALL, this.props.selectMailItemsAction, '0');
        }

        if (
            isMediaSizeLessThanLG(prevProps.mediaSize) &&
            !isMediaSizeLessThanLG(this.props.mediaSize) &&
            !isTouchScreen() &&
            this.props.touchSelectActive
        ) {
            this.props.setTouchSelectActive(false);
        }

        if (
            !isMediaSizeLessThanLG(prevProps.mediaSize) &&
            isMediaSizeLessThanLG(this.props.mediaSize) &&
            this.props.selectedDocuments.length > 0
        ) {
            this.props.setTouchSelectActive(true);
        }

        if (
            (!isMediaSizeLessThanMD(prevProps.mediaSize) && isMediaSizeLessThanMD(this.props.mediaSize)) ||
            (isMediaSizeLessThanMD(prevProps.mediaSize) && !isMediaSizeLessThanMD(this.props.mediaSize))
        ) {
            this.props.selectMailsAction(SelectAllItems.NONE);
            this.props.setSelectedDocuments([]);
            this.props.setTouchSelectActive(false);
        }
    }

    handleItemSelected = (type: MenuBarButtonType, action: string, actionValue: string): void => {
        const documentsShownList = (
            this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documents
        ).slice(0, this.props.currentShownDocumentsCount);
        switch (type) {
            case MenuBarButtonType.SELECT_ALL:
                switch (action as SelectAllItems) {
                    case SelectAllItems.CHECK_ALL:
                        this.props.setSelectedDocuments(
                            documentsShownList.map((document: IMessage) => {
                                return { id: document.id, version: document.version } as ISelectedMessage;
                            })
                        );
                        this.props.selectMailsAction(action as SelectAllItems);
                        break;

                    case SelectAllItems.CHECK_READ:
                        const readDocuments = documentsShownList.filter((document: IMessage) => {
                            return document.read;
                        });
                        this.props.setSelectedDocuments(readDocuments);
                        this.props.selectMailsAction(action as SelectAllItems);
                        break;

                    case SelectAllItems.CHECK_UNREAD:
                        const unreadDocuments = documentsShownList.filter((document: IMessage) => {
                            return !document.read;
                        });
                        this.props.setSelectedDocuments(unreadDocuments);
                        this.props.selectMailsAction(action as SelectAllItems);
                        break;

                    case SelectAllItems.UNCHECK_ALL:
                        this.props.setSelectedDocuments([]);
                        this.props.selectMailsAction(action as SelectAllItems);
                        break;
                }
                break;

            case MenuBarButtonType.MOVE_TO_FOLDER:
                if (this.props.onFolderAction) {
                    this.props.onFolderAction(action as MoveToFolderItems, actionValue);
                }
                break;

            case MenuBarButtonType.MORE_ACTIONS:
                switch (action as MoreActionsItems) {
                    case MoreActionsItems.FORWARD:
                    case MoreActionsItems.REPLY:
                    case MoreActionsItems.ADD_NOTE:
                    case MoreActionsItems.EDIT_NOTE:
                    case MoreActionsItems.INFORMATION:
                    case MoreActionsItems.DOWNLOAD:
                    case MoreActionsItems.MARK_AS_READ:
                    case MoreActionsItems.MARK_AS_UNREAD:
                    case MoreActionsItems.ADD_FLAG:
                    case MoreActionsItems.REMOVE_FLAG:
                    case MoreActionsItems.TEXT_PRINT:
                    case MoreActionsItems.DELETE:
                        if (this.props.onMoreAction) {
                            this.props.onMoreAction(action as MoreActionsItems, actionValue);
                        }
                        break;
                }
                break;
        }
    };

    getEnableDisableButton = (type: MenuBarButtonType): boolean => {
        const isSelected = this.props.selectedDocuments.length > 0;
        const inTouchMode = isTouchScreen() || isMediaSizeLessThanMD(this.props.mediaSize);

        const isOneDocumentView = this.props.isOneDocumentView;
        if (
            inTouchMode &&
            !isOneDocumentView &&
            !isSelected &&
            !this.props.touchSelectActive &&
            type !== MenuBarButtonType.MAIL_SEND
        ) {
            return false;
        }
        const onlyOneDocumentSelected =
            this.props.selectedDocuments.length === 1 || this.props.oneDocumentView || false;

        const atLeastOneDocumentSelected =
            this.props.selectedDocuments.length > 0 || this.props.oneDocumentView || false;

        const selectedDocumentList = getSelectedDocuments(
            this.props.isSenderClicked ? this.props.selectSenderFolder.originalRecords : this.props.documents,
            this.props.selectedDocuments
        );

        const isDraft =
            selectedDocumentList.length > 0 &&
            selectedDocumentList.some((doc) => doc.messageState === MessageState.DRAFT);

        switch (type) {
            case MenuBarButtonType.SELECT_ALL:
                return (
                    (this.props.isSenderClicked
                        ? this.props.selectSenderFolder.originalRecords.length > 0
                        : this.props.documents.length > 0) || this.props.touchSelectActive
                );

            case MenuBarButtonType.MAIL_REPLY:
                return (
                    onlyOneDocumentSelected &&
                    IsMailReplyAllowed(selectedDocumentList, this.props.access, this.props.language)
                );
            case MenuBarButtonType.MAIL_SEND:
                return this.IsMailSendAllowed();

            case MenuBarButtonType.MAIL_FORWARD:
                return this.IsMailForwardAllowed(isDraft, atLeastOneDocumentSelected, selectedDocumentList);

            case MenuBarButtonType.MAIL_ARCHIVE:
                return atLeastOneDocumentSelected;

            case MenuBarButtonType.MOVE_TO_FOLDER:
                return this.IsMoveToFolderAllowed(isDraft, atLeastOneDocumentSelected);

            case MenuBarButtonType.MAIL_DELETE:
                return IsMailDeleteAllowed(
                    atLeastOneDocumentSelected,
                    this.props.access,
                    this.props.folders,
                    this.props.selectedFolder
                );

            case MenuBarButtonType.MORE_ACTIONS:
                return atLeastOneDocumentSelected;

            case MenuBarButtonType.MAIL_PRINT:
                return onlyOneDocumentSelected && this.props.enablePrint;

            case MenuBarButtonType.SAVE:
                return atLeastOneDocumentSelected;

            case MenuBarButtonType.CLOSE:
                return true;

            case MenuBarButtonType.ADD_EDIT_NOTE:
                return onlyOneDocumentSelected;

            case MenuBarButtonType.MORE_INFORMATION:
                return onlyOneDocumentSelected;

            default:
                return false;
        }
    };

    isCloseButtonAlone = (config: any): boolean => {
        if (this.props.hideClose) {
            return false;
        }

        return !(
            !this.props.hideSelectAll ||
            !this.props.hideMore ||
            !this.props.hidePrint ||
            (!this.props.hideMoveToFolder && config.features.message.move) ||
            (!this.props.hideReply && config.features.message.reply) ||
            (!this.props.hideForward && config.features.message.forward) ||
            (!this.props.hideArchive && config.features.message.archive) ||
            (!this.props.hideDelete && config.features.message.delete) ||
            (!this.props.hideSend && config.features.message.send) ||
            !this.props.hideSave
        );
    };

    countTopForMobile = (): number => {
        return mobileHeaderHeight; //this.props.scrollTop > mobileHeaderHeight ? 0 : mobileHeaderHeight;
    };

    getPositionClassForMobile = (): string => {
        return ''; // this.props.scrollTop < mobileHeaderHeight ? 'position-absolute' : '';
    };

    private IsMailSendAllowed(): boolean {
        if (!this.props.access) {
            return isTouchScreen() || isMediaSizeLessThanMD(this.props.mediaSize);
        }
        return (
            isAllowed(this.props.access, PermissionType.SENDMESSAGE) &&
            (isTouchScreen() || isMediaSizeLessThanMD(this.props.mediaSize))
        );
    }

    private IsMoveToFolderAllowed(isDraft: boolean, multipleSelected: boolean): boolean {
        if (!this.props.access) {
            return multipleSelected;
        }
        return isAllowed(this.props.access, PermissionType.MOVEMESSAGEBETWEENFOLDERS) && multipleSelected;
    }

    private isDraftAndMultipleSelected(isDraft: boolean, multipleSelected: boolean): boolean {
        return !isDraft && multipleSelected;
    }

    private IsMailForwardAllowed(
        isDraft: boolean,
        multipleSelected: boolean,
        selectedDocumentList: IMessage[]
    ): boolean {
        if (!this.props.access) {
            return (
                this.checkIsDraftAndIsSentFolder(isDraft, multipleSelected) &&
                !selectedDocumentList.some(this.selectedDocumentAlreadyForwarded)
            );
        }

        const isForward = selectedDocumentList.filter((item) => !item.forward);

        return (
            (isAllowed(this.props.access, PermissionType.FORWARDTOEMAIL) ||
                isAllowed(this.props.access, PermissionType.FORWARDTONGDPIDENTITY)) &&
            this.checkIsDraftAndIsSentFolder(isDraft, multipleSelected) &&
            !selectedDocumentList.some(this.selectedDocumentAlreadyForwarded) &&
            isForward.length == 0
        );
    }

    private selectedDocumentAlreadyForwarded = (message: IMessage): boolean => Boolean(message?.forwardData?.id);

    private checkIsDraftAndIsSentFolder(isDraft: boolean, multipleSelected: boolean): boolean {
        return (
            this.isDraftAndMultipleSelected(isDraft, multipleSelected) &&
            !isSentFolder(this.props.selectedFolder.id, this.props.folders)
        );
    }

    public render(): JSX.Element {
        const selectorEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.SELECT_ALL);
        const replyButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_REPLY);
        const forwardButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_FORWARD);
        const archiveButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_ARCHIVE);
        const moveButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MOVE_TO_FOLDER);
        const deleteButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_DELETE);
        const saveButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.SAVE);
        const moreButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MORE_ACTIONS);
        const printButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_PRINT);
        const sendButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.MAIL_SEND);
        const closeButtonEnabled: boolean = this.getEnableDisableButton(MenuBarButtonType.CLOSE);
        const inTouchMode = isMediaSizeLessThanMD(this.props.mediaSize);
        const isSmall = isMediaSizeLessThanMD(this.props.mediaSize);
        return (
            <ConfigContext.Consumer>
                {(config): JSX.Element =>
                    this.isCloseButtonAlone(config) ? null : (
                        <div className={`${styles.mailboxTopActions} d-flex ${this.props.className}`}>
                            <div className={`${styles.actionElement} d-flex flex-row`}>
                                {inTouchMode || this.props.hideSelectAll ? null : (
                                    <>
                                        <MenuBarButton
                                            useButtonLoader={false}
                                            useImage={false}
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.SELECT_ALL}
                                            buttonEnabled={selectorEnabled}
                                        />
                                        <div className={styles.space} />
                                    </>
                                )}
                                <div className={`d-flex ${isSmall} w-100 justify-content-end`}>
                                    {!this.props.hideSend &&
                                    config.features.message.send &&
                                    isSmall &&
                                    !this.props.isOneDocumentView ? (
                                        <MenuBarButton
                                            useButtonLoader={false}
                                            useImage
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.MAIL_SEND}
                                            buttonEnabled={sendButtonEnabled}
                                        />
                                    ) : null}
                                    {!this.props.hideReply && config.features.message.reply ? (
                                        <MenuBarButton
                                            useButtonLoader
                                            useImage
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.MAIL_REPLY}
                                            buttonEnabled={replyButtonEnabled}
                                        />
                                    ) : null}
                                    {!isSmall ? (
                                        <>
                                            {!this.props.hideForward && config.features.message.forward ? (
                                                <MenuBarButton
                                                    useButtonLoader
                                                    useImage
                                                    onItemSelected={this.handleItemSelected}
                                                    onButtonAction={this.props.onMenuBarButtonAction}
                                                    buttonType={MenuBarButtonType.MAIL_FORWARD}
                                                    buttonEnabled={forwardButtonEnabled}
                                                />
                                            ) : null}
                                            {!this.props.hideArchive && config.features.message.archive ? (
                                                <MenuBarButton
                                                    useButtonLoader
                                                    useImage={false}
                                                    onItemSelected={this.handleItemSelected}
                                                    onButtonAction={this.props.onMenuBarButtonAction}
                                                    buttonType={MenuBarButtonType.MAIL_ARCHIVE}
                                                    buttonEnabled={archiveButtonEnabled}
                                                />
                                            ) : null}
                                        </>
                                    ) : null}
                                    {!this.props.isOneDocumentView &&
                                        this.props.selectedDocuments.length > 0 &&
                                        config.features.message.save && (
                                            <MenuBarButton
                                                useImage={false}
                                                onItemSelected={this.handleItemSelected}
                                                onButtonAction={this.props.onMenuBarButtonAction}
                                                buttonType={MenuBarButtonType.SAVE}
                                                buttonEnabled={saveButtonEnabled}
                                                useButtonLoader={false}
                                            />
                                        )}
                                    {!isSmall ? (
                                        !this.props.hidePrint && config.features.message.print ? (
                                            <MenuBarButton
                                                useButtonLoader={false}
                                                useImage={false}
                                                onItemSelected={this.handleItemSelected}
                                                onButtonAction={this.props.onMenuBarButtonAction}
                                                buttonType={MenuBarButtonType.SAVE}
                                                buttonEnabled={printButtonEnabled}
                                            />
                                        ) : null
                                    ) : null}
                                    {!this.props.hideMoveToFolder && config.features.message.move ? (
                                        <MenuBarButton
                                            useButtonLoader={false}
                                            useImage
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.MOVE_TO_FOLDER}
                                            buttonEnabled={moveButtonEnabled}
                                        />
                                    ) : null}
                                    {!isSmall ? (
                                        !this.props.hidePrint && config.features.message.print ? (
                                            <MenuBarButton
                                                useButtonLoader={false}
                                                useImage={false}
                                                onItemSelected={this.handleItemSelected}
                                                onButtonAction={this.props.onMenuBarButtonAction}
                                                buttonType={MenuBarButtonType.MAIL_PRINT}
                                                buttonEnabled={printButtonEnabled}
                                            />
                                        ) : null
                                    ) : null}
                                    {!this.props.hideDelete && config.features.message.delete ? (
                                        <MenuBarButton
                                            useButtonLoader={false}
                                            useImage
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.MAIL_DELETE}
                                            buttonEnabled={deleteButtonEnabled}
                                        />
                                    ) : null}
                                    {!this.props.hideMore ? (
                                        <MenuBarButton
                                            transparentBackGround={this.props.oneDocumentView}
                                            showEndBorder
                                            useButtonLoader={false}
                                            useImage={false}
                                            onItemSelected={this.handleItemSelected}
                                            onButtonAction={this.props.onMenuBarButtonAction}
                                            buttonType={MenuBarButtonType.MORE_ACTIONS}
                                            buttonEnabled={moreButtonEnabled}
                                        />
                                    ) : null}
                                </div>
                                {!this.props.hideClose ? (
                                    <MenuBarButton
                                        showEndBorder
                                        useButtonLoader={false}
                                        useImage={false}
                                        onItemSelected={this.handleItemSelected}
                                        onButtonAction={this.props.onMenuBarButtonAction}
                                        buttonType={MenuBarButtonType.CLOSE}
                                        buttonEnabled={closeButtonEnabled}
                                    />
                                ) : null}
                            </div>
                        </div>
                    )
                }
            </ConfigContext.Consumer>
        );
    }
}

const mapStateToProps = ({ mailbox, menubar, appReadouts, documentPreview, user, sender }: IRootState) => {
    return {
        selectedDocuments: mailbox.selectedDocuments,
        documents: mailbox.selectedSender == null ? mailbox.documentList : mailbox.filteredDocumentList,
        mediaSize: appReadouts.mediaSize,
        scrollTop: appReadouts.scrollTop,
        selectMailItemsAction: menubar.selectMailItemsAction,
        selectedFolder: mailbox.selectedFolder,
        folders: mailbox.folderList,
        currentShownDocumentsCount: mailbox.currentShownDocumentsCount,
        touchSelectActive: menubar.touchSelectActive,
        enablePrint: menubar.enablePrint,
        isOneDocumentView: documentPreview.previewShown,
        language: getCurrentLanguage(appReadouts.supportedLanguages, user.languageId).isoCode,
        access: mailbox.mailbox.access,
        mailboxId: mailbox.mailbox.id,
        isSenderClicked: sender.isSenderClicked,
        selectSenderFolder: mailbox.selectSenderFolder,
    };
};

const mapDispatcherToProps = (dispatch: Dispatch) => {
    return {
        selectMailsAction: (selectedAction: SelectAllItems) => dispatch(selectMailsAction(selectedAction)),
        setSelectedDocuments: (selectedDocuments: Array<ISelectedMessage>) =>
            dispatch(setSelectedDocuments(selectedDocuments)),
        setTouchSelectActive: (active: boolean) => dispatch(setTouchSelectActive(active)),
    };
};

type ReduxType = IMenuBarProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatcherToProps>;

export default connect(mapStateToProps, mapDispatcherToProps)(MenuBar);
